import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

import { CommonModule, DatePipe } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import { PDFModule, SchedulerModule } from '@progress/kendo-angular-scheduler';
import { ExternalUiSharedModule } from '../external-ui-shared/external-ui-shared.module';
import { AddCarBrandDictionaryItemComponent } from './components/add-car-brand-dictionary-item/add-car-brand-dictionary-item.component';
import { AddCarModelDictionaryItemComponent } from './components/add-car-model-dictionary-item/add-car-model-dictionary-item.component';
import { AddCityDictionaryItemComponent } from './components/add-city-dictionary-item/add-city-dictionary-item.component';
import { AvatarIconComponent } from './components/avatar-icon/avatar-icon.component';
import { ConfirmationDialogComponent } from './components/confirmation-dialog/confirmation-dialog.component';
import { DatepickerDialogComponent } from './components/datepicker-dialog/datepicker-dialog.component';
import { ExternalLinkComponent } from './components/external-link/external-link.component';
import { LoadingPanelComponent } from './components/loading-panel/loading-panel.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { SchedulerComponent } from './components/scheduler/scheduler.component';
import { TextDialogComponent } from './components/text-dialog/text-dialog.component';
import { CustomDateAdapter } from './controls/custom-date-adapter';
import { CheckboxButtonInputComponent } from './controls/inputs/checkbox-button-input/checkbox-button-input.component';
import { CheckboxInputComponent } from './controls/inputs/checkbox-input/checkbox-input.component';
import { DateInputComponent } from './controls/inputs/date-input/date-input.component';
import { DateTimeInputComponent } from './controls/inputs/date-time-input/date-time-input.component';
import { IbanInputComponent } from './controls/inputs/iban-input/iban-input.component';
import { MaskedTextInputComponent } from './controls/inputs/masked-text-input/masked-text-input.component';
import { NumericInputComponent } from './controls/inputs/numeric-input/numeric-input.component';
import { NumericPlusMinusInputComponent } from './controls/inputs/numeric-plus-minus-input/numeric-plus-minus-input.component';
import { TextInputComponent } from './controls/inputs/text-input/text-input.component';
import { TextareaInputComponent } from './controls/inputs/textarea-input/textarea-input.component';
import { UrlTextInputComponent } from './controls/inputs/url-text-input/url-text-input.component';
import { CarBrandComboSelectComponent } from './controls/selects/car-brand-combo-select/car-brand-combo-select.component';
import { CarModelComboSelectComponent } from './controls/selects/car-model-combo-select/car-model-combo-select.component';
import { CityComboSelectComponent } from './controls/selects/city-combo-select/city-combo-select.component';
import { ClaimTagComboSelectComponent } from './controls/selects/claim-tag-combo-select/claim-tag-combo-select.component';
import { ListComboSelectComponent } from './controls/selects/list-combo-select/list-combo-select.component';
import { ListMultiselectComponent } from './controls/selects/list-multiselect/list-multiselect.component';
import { ListSelectComponent } from './controls/selects/list-select/list-select.component';
import { StaticDictionaryMultiselectComponent } from './controls/selects/static-dictionary-multiselect/static-dictionary-multiselect.component';
import { StaticDictionarySelectComponent } from './controls/selects/static-dictionary-select/static-dictionary-select.component';
import { StaticDictionaryTextAutocompleteComponent } from './controls/selects/static-dictionary-text-autocomplete/static-dictionary-text-autocomplete.component';
import { BackButtonDirective } from './directives/back-button.directive';
import { NoProfilePageComponent } from './pages/no-profile-page/no-profile-page.component';
import { BooleanTextPipe } from './pipes/BooleanTextPipe';
import { DurationMinutesPipe } from './pipes/DurationMinutesPipe';
import { EmptyTextReplacePipe } from './pipes/EmptyTextReplacePipe';
import { FileSizePipe } from './pipes/FileSizePipe';
import { FileTypeIconPipe } from './pipes/FileTypeIconPipe';
import { StaticDictionaryCaptionPipe } from './pipes/StaticDictionaryCaptionPipe';
import { DispatchProfilePageComponent } from './pages/dispatch-profile-page/dispatch-profile-page.component';
import { PeriodicalValueInputComponent } from './controls/inputs/periodical-value-input/periodical-value-input.component';
import { EditPeriodicalValueComponent } from './controls/custom/edit-periodical-value/edit-periodical-value.component';
import { NotAuthorizedComponent } from './pages/not-authorized/not-authorized.component';
import { ExportToExcelComponent } from './components/export-to-excel/export-to-excel.component';
import { CustomDropDownButtonComponent } from './controls/custom/custom-drop-down-button/custom-drop-down-button.component';
import { SelectLocalizationComponent } from './controls/custom/select-localization/select-localization.component';
import { HtmlEditorInputComponent } from './controls/inputs/html-editor-input/html-editor-input.component';
import { SafePipe } from './pipes/SafePipe';
import { ShowInfoTextDialogComponentComponent } from './components/show-info-text-dialog-component/show-info-text-dialog-component.component';
import { CityComboSelectDialogComponent } from './controls/selects/city-combo-select-dialog/city-combo-select-dialog.component';
import { StaticDictionarySelectDialogComponent } from './controls/selects/static-dictionary-select-dialog/static-dictionary-select-dialog.component';
import { CheckboxDialogComponent } from './components/checkbox-dialog/checkbox-dialog.component';
import { HighlightMatchesPipe } from './components/ocr-scanner/pipes/highlight.pipe';
import { OcrScannerComponent } from './components/ocr-scanner/ocr-scanner';
import { PolishIbanPipe } from './pipes/PolishIbanPipe';
import { SplittableTextInputComponent } from './controls/inputs/splittable-text-input/splittable-text-input.component';
import { DisplayTextComponent } from './components/display-text/display-text.component';
import { ShowHelpTextComponent } from './components/show-help-text/show-help-text.component';
import { EditHelpTextDialogComponent } from './components/edit-help-text-dialog/edit-help-text-dialog.component';
import { EmailSelectComponent } from './controls/selects/email-select/email-select.component';

const uiSharedComponents = [
  AvatarIconComponent,
  NotFoundComponent,
  NoProfilePageComponent,
  NotAuthorizedComponent,
  DispatchProfilePageComponent,
  LoadingPanelComponent,
  TextInputComponent,
  NumericInputComponent,
  DateInputComponent,
  DateTimeInputComponent,
  TextareaInputComponent,
  MaskedTextInputComponent,
  StaticDictionarySelectComponent,
  CheckboxInputComponent,
  ListSelectComponent,
  StaticDictionaryMultiselectComponent,
  ListComboSelectComponent,
  ListMultiselectComponent,
  NumericPlusMinusInputComponent,
  TextInputComponent,
  NumericInputComponent,
  CarBrandComboSelectComponent,
  CarModelComboSelectComponent,
  CityComboSelectComponent,
  ClaimTagComboSelectComponent,
  AddCityDictionaryItemComponent,
  AddCarBrandDictionaryItemComponent,
  AddCarModelDictionaryItemComponent,
  StaticDictionaryTextAutocompleteComponent,
  ConfirmationDialogComponent,
  SchedulerComponent,
  UrlTextInputComponent,
  ExternalLinkComponent,
  DatepickerDialogComponent,
  ShowInfoTextDialogComponentComponent,
  TextDialogComponent,
  IbanInputComponent,
  CheckboxButtonInputComponent,
  PeriodicalValueInputComponent,
  EditPeriodicalValueComponent,
  ExportToExcelComponent,
  CustomDropDownButtonComponent,
  SelectLocalizationComponent,
  HtmlEditorInputComponent,
  CityComboSelectDialogComponent,
  StaticDictionarySelectDialogComponent,
  CheckboxDialogComponent,
  OcrScannerComponent,
  SplittableTextInputComponent,
  DisplayTextComponent,
  ShowHelpTextComponent,
  EditHelpTextDialogComponent, 
  EmailSelectComponent
];

const uiSharedPipes = [
  BooleanTextPipe,
  FileSizePipe,
  FileTypeIconPipe,
  StaticDictionaryCaptionPipe,
  EmptyTextReplacePipe,
  DurationMinutesPipe,
  SafePipe,
  HighlightMatchesPipe,
  PolishIbanPipe
];

const uiSharedDirectives = [BackButtonDirective];

const resharedModules = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  ExternalUiSharedModule,
  NgxExtendedPdfViewerModule,
  SchedulerModule,
  PDFModule,
];

@NgModule({
  declarations: [uiSharedPipes, uiSharedComponents, uiSharedDirectives],
  imports: [
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ExternalUiSharedModule,
    NgxExtendedPdfViewerModule,
    MtxDatetimepickerModule,
    MtxNativeDatetimeModule,
    SchedulerModule,
    PDFModule,
  ],
  exports: [
    uiSharedPipes,
    uiSharedComponents,
    resharedModules,
    uiSharedDirectives,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    MatDatepickerModule,
    DatePipe,
  ],
})
export class SharedModule { }
